import * as React from "react";
import { Upload } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { InfoTooltip } from "../InfoTooltip";
import { useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/notificationDrawerSlice";
import { formSet, formSetValue, formTriggerValidation } from "../../pages/gvc/testNewForm/utils";

export interface ImportButtonRawProps {
  formId: string;
  fieldName: string;
  actionLabel?: string;
  accept?: string;
  explanation?: React.ReactNode;
  existingHasContent?: boolean;
}

export function ImportButtonRaw({
  formId,
  fieldName,
  actionLabel = "Import",
  accept = "*",
  explanation,
  existingHasContent = false,
}: ImportButtonRawProps) {
  const dispatch = useAppDispatch();
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounter = React.useRef(0);

  const handleImport = React.useCallback(
    async (file: File) => {
      try {
        const content = await file.text();

        if (!content || content.trim().length === 0) {
          dispatch(
            addNotification({
              page,
              action: actionLabel,
              success: false,
              message: "File is empty",
              details: {
                fileName: file.name,
              },
            })
          );
          return;
        }

        formSetValue(formId, fieldName, content);
        formSet(formId, `fieldStates.${fieldName}.modified`, true);
        formTriggerValidation(formId);

        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: true,
            message: existingHasContent ? "File imported (previous content replaced)" : "File imported successfully",
            details: {
              fileName: file.name,
              contentLength: content.length,
            },
          })
        );
      } catch (e) {
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: `Failed to read file: ${(e as Error).message}`,
          })
        );
      }
    },
    [formId, fieldName, actionLabel, existingHasContent, dispatch, page]
  );

  const uploadProps = {
    showUploadList: false,
    accept,
    beforeUpload: async (file: File) => {
      await handleImport(file);
      return false as const;
    },
  };

  return (
    <div className="inline-flex items-center">
      <Upload {...uploadProps} className="inline-block [&_.ant-upload]:block">
        <div
          className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded cursor-pointer transition-all text-sm"
          style={{
            borderColor: isDragOver ? "var(--color-primary)" : "var(--color-border)",
            color: isDragOver ? "var(--color-primary)" : "var(--color-label)",
            backgroundColor: isDragOver ? "var(--color-primary-faint)" : "transparent",
          }}
          onMouseEnter={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-primary)";
              e.currentTarget.style.color = "var(--color-primary)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.color = "var(--color-label)";
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            dragCounter.current++;
            setIsDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => {
            dragCounter.current--;
            if (dragCounter.current === 0) setIsDragOver(false);
          }}
          onDrop={() => {
            dragCounter.current = 0;
            setIsDragOver(false);
          }}
        >
          <ImportOutlined />
          <span>Drop file or click to import</span>
        </div>
      </Upload>
      {explanation && <InfoTooltip title={explanation} style={{ marginLeft: 8 }} />}
    </div>
  );
}
